/**
 * Тексты paywall-модалки по поводу показа (docs/billing/ui.md §2).
 * Формулировки авторские, из прайс-документа владельца — не переписывать своими словами.
 */
import { PLANS, type PaywallReason, type PlanId } from '@sloy/shared';

export interface PaywallCopy {
  title: string;
  body: string;
  cta: string;
  secondary: string;
}

const FALLBACK: PaywallCopy = {
  title: 'Доступно на тарифе выше',
  body: 'Эта возможность входит в платные тарифы «Слоя».',
  cta: 'Посмотреть тарифы',
  secondary: 'Закрыть',
};

const TEXTS: Partial<Record<PaywallReason, PaywallCopy>> = {
  projects: {
    title: 'Три проекта уже созданы',
    body: 'На бесплатном тарифе можно создать до трёх проектов. В PRO количество проектов не ограничено, а для данных доступно 10 ГБ.',
    // Цену с кнопки убрали: PRO считается за место (решение владельца 2026-08-16),
    // и «за 1 490 ₽» обещало бы фиксированную сумму команде из пяти редакторов.
    cta: 'Перейти на PRO',
    secondary: 'Вернуться к проектам',
  },
  // Текст не из прайс-документа (там только строка «1 редактор, зрители и комментаторы
  // бесплатно») — согласовать с владельцем.
  team: {
    title: 'Команда — на тарифе PRO',
    body: 'На бесплатном тарифе карты редактирует один человек — владелец команды. В PRO редакторов сколько угодно, оплата за каждого. Зрителей и комментаторов можно приглашать бесплатно на любом тарифе.',
    cta: 'Перейти на PRO',
    secondary: 'Не сейчас',
  },
  storage: {
    title: 'Хранилище заполнено',
    body: 'Вы использовали доступные 250 МБ. В PRO входит 10 ГБ для файлов, изображений и геоданных.',
    cta: 'Получить 10 ГБ в PRO',
    secondary: 'Управлять файлами',
  },
  tile_formats: {
    title: 'Для этого файла нужен PRO',
    body: 'В PRO можно работать с большими наборами данных, PMTiles, векторными тайлами и растровыми XYZ-слоями.',
    cta: 'Загрузить файл с PRO',
    secondary: 'Выбрать другой файл',
  },
  format_geopackage: {
    title: 'Для этого файла нужен PRO',
    body: 'В PRO можно загружать GeoPackage и Parquet, а также работать с большими наборами данных, PMTiles и векторными тайлами.',
    cta: 'Загрузить файл с PRO',
    secondary: 'Выбрать другой файл',
  },
  sheet_editor: {
    title: 'Редактируйте данные прямо в Слое',
    body: 'Полный табличный редактор доступен в PRO. Меняйте атрибуты и сразу смотрите результат на карте.',
    cta: 'Открыть редактор с PRO',
    secondary: 'Продолжить на карте',
  },
  custom_fonts: {
    title: 'Свои шрифты — в PRO',
    body: 'В PRO можно загружать собственные шрифты и использовать их в оформлении и публикации карт.',
    cta: 'Подключить PRO',
    secondary: 'Продолжить со стандартными',
  },
  integrations: {
    title: 'Подключите данные, которые обновляются сами',
    body: 'В PRO MAX можно подключить Google Sheets, REST, WMS и WFS и настроить обновление данных по расписанию.',
    cta: 'Подключить PRO MAX',
    secondary: 'Импортировать файл вручную',
  },
  subdomain: {
    title: 'Соберите проекты команды на своём поддомене',
    body: 'PRO MAX добавляет рабочее пространство с собственным поддоменом, красивыми адресами карт и управлением доступом.',
    cta: 'Рассчитать PRO MAX',
    secondary: 'Продолжить в обычном пространстве',
  },
  branding_optional: {
    title: 'Публикация без бренда «Слоя»',
    body: 'В PRO можно скрывать логотип «Слоя» на опубликованных картах.',
    cta: 'Подключить PRO',
    secondary: 'Оставить как есть',
  },
  noindex_toggle: {
    title: 'Управляйте видимостью карты в поиске',
    body: 'В PRO можно разрешить или запретить индексацию каждой публичной карты поисковыми системами.',
    cta: 'Настроить индексацию с PRO',
    secondary: 'Оставить текущую настройку',
  },
  map_password: {
    title: 'Ограничьте доступ к публичной карте',
    body: 'В PRO MAX опубликованную карту можно защитить паролем и передать ссылку только нужным людям.',
    cta: 'Защитить карту паролем',
    secondary: 'Оставить карту открытой',
  },
  mcp: {
    title: 'Подключите ИИ-агентов к картам',
    body: 'В PRO MAX Claude, Codex, Cursor и другие агенты через MCP создают карты, загружают данные и настраивают оформление от вашего имени.',
    cta: 'Подключить PRO MAX',
    secondary: 'Не сейчас',
  },
  analytics: {
    title: 'Узнайте, как смотрят ваши карты',
    body: 'PRO MAX показывает статистику посещений и просмотров опубликованных проектов.',
    cta: 'Открыть аналитику с PRO MAX',
    secondary: 'Вернуться к карте',
  },
};

// format_parquet делит текст с format_geopackage — в прайсе это один paywall
// «Профессиональные форматы».
TEXTS.format_parquet = TEXTS.format_geopackage;

/**
 * Упор в лимит гостей (docs/workspaces/plan.md §3.7). Текст не из прайс-документа —
 * согласовать с владельцем. Цифры — из каталога: лимит у каждого тарифа свой, и
 * зашитое «до 25» разошлось бы с `PLANS` при первой же правке сетки.
 */
function guestsCopy(requiredPlan: PlanId): PaywallCopy {
  const limit = PLANS[requiredPlan].limits.guests;
  const title = PLANS[requiredPlan].title;
  return {
    title: 'Места для гостей закончились',
    body: `Гости — люди не из команды, которых позвали в отдельные карты. Их число зависит от тарифа: ${
      limit === null ? `на тарифе «${title}» оно не ограничено` : `в ${title} можно позвать до ${limit}`
    }. Участников команды этот лимит не касается.`,
    cta: requiredPlan === 'enterprise' ? 'Посмотреть тарифы' : `Перейти на ${title}`,
    secondary: 'Не сейчас',
  };
}

/**
 * Тексты по поводу. Для упора в лимит хранилища на платном плане (PRO → PRO MAX)
 * прайсовый текст про 250 МБ вводил бы в заблуждение — подменяем цифры.
 */
export function paywallCopy(reason: PaywallReason, requiredPlan: PlanId): PaywallCopy {
  if (reason === 'guests') {
    return guestsCopy(requiredPlan);
  }
  if (reason === 'storage' && requiredPlan === 'pro_max') {
    return {
      title: 'Хранилище заполнено',
      body: 'Вы использовали доступные 10 ГБ. В PRO MAX входит 50 ГБ и повышенные лимиты просмотров.',
      cta: 'Получить 50 ГБ в PRO MAX',
      secondary: 'Управлять файлами',
    };
  }
  return TEXTS[reason] ?? FALLBACK;
}
